import { useState, useContext, useEffect, useMemo } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { keyframes } from "@mui/system";
import { useTranslation } from "react-i18next";
import { SelectedMeeting } from "../../../pages/notes/meetingNotes";
import EditableMeetingInfo from "../EditableMeetingInfo";
import CreateMeetingCard from "./CreateMeetingCard";
import ConfirmationPanel from "../../confirmationPanel";
import { Context } from "../../context";
import { AppContext } from "../../../models/applicationState";
import { Meeting } from "../../../models/meeting";
import * as listActions from "../../../actions/listActions";
import { ListActions } from "../../../actions/listActions";
import { bindActionCreators } from "../../../actions/actionCreators";
import {
  formatLocalDateDisplay,
  formatLocalTimeDisplay,
  formatMeetingTimeRange,
  hasMeaningfulTime,
  toLocalDateInputValue,
} from "../../../util/meetingDateTime";

interface SelectMeetingStepProps {
  selectedMeeting: SelectedMeeting | null;
  onSelectMeeting: (meeting: SelectedMeeting) => void;
  onGoToTakeNotes: (meeting: SelectedMeeting) => void;
  onMeetingChange: (meeting: SelectedMeeting) => void;
  onError: (message: string, error?: unknown) => void;
}

/**
 * Convert Pinnacle Meeting to SelectedMeeting display format
 */
const meetingToSelected = (meeting: Meeting): SelectedMeeting => {
  const dateStr =
    toLocalDateInputValue(meeting.date) || toLocalDateInputValue(new Date());
  const derivedStartTime =
    meeting.startTime ||
    (meeting.eventId && hasMeaningfulTime(meeting.date)
      ? formatLocalTimeDisplay(meeting.date)
      : "");

  return {
    id: meeting.id || "",
    title: meeting.name,
    date: dateStr,
    startTime: derivedStartTime,
    endTime: meeting.endTime || "",
    attendees: meeting.participants?.length || 0,
    originalMeeting: meeting,
  };
};

const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translate3d(-28px, 0, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const SelectMeetingStep = ({
  selectedMeeting,
  onSelectMeeting,
  onGoToTakeNotes,
  onMeetingChange,
  onError,
}: SelectMeetingStepProps) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context) as AppContext;
  const [meetings, setMeetings] = useState<SelectedMeeting[]>([]);
  const [showCreateCard, setShowCreateCard] = useState(false);
  const [meetingToDelete, setMeetingToDelete] =
    useState<SelectedMeeting | null>(null);

  // Create list actions for saving
  const actions = useMemo(
    () => bindActionCreators(listActions, dispatch) as unknown as ListActions,
    [dispatch],
  );

  // Load meetings from context - only show meetings created by current user that are not published
  useEffect(() => {
    if (state.lists && state.invoker) {
      const converted = state.lists
        .filter(
          (m) =>
            !m.archived &&
            //currently checking if the creator is the currentuserid(invoker) doesn't work because of the way meetings are created via the API
            //so for now: showing all meetings that are only created by the user and not archived or published
            //TODO m.creator === currentUserId &&
            m.status !== "published",
        )
        .map(meetingToSelected)
        .sort((a, b) => a.date.localeCompare(b.date)); //TODO: Remove if sorting is unwanted or handled by the API?
      setMeetings(converted);
    }
  }, [state.lists, state.invoker]);

  const getDisplayMeeting = (meeting: SelectedMeeting) => {
    if (selectedMeeting?.id === meeting.id) {
      return selectedMeeting;
    }
    // Always use the latest version from the meetings array (from API)
    // This ensures we have the most up-to-date data including startTime/endTime
    const latestVersion = meetings.find((m) => m.id === meeting.id) || meeting;
    return latestVersion;
  };

  const handleCardMeetingChange = (updatedMeeting: SelectedMeeting) => {
    setMeetings((previousMeetings) =>
      previousMeetings.map((meeting) =>
        meeting.id === updatedMeeting.id ? updatedMeeting : meeting,
      ),
    );
    onMeetingChange(updatedMeeting);
  };

  const handleDeleteMeeting = async () => {
    if (!meetingToDelete?.id) return;
    try {
      await actions.remove(meetingToDelete.id);
    } catch (error) {
      onError(t("meetingNotes.selectMeeting.errors.deleteMeeting"), error);
    }
    setMeetingToDelete(null);
  };

  const handleCreateMeeting = async (newMeeting: SelectedMeeting) => {
    if (newMeeting.originalMeeting) {
      try {
        const saved = await actions.save(newMeeting.originalMeeting);
        if (saved && saved.id) {
          await actions.list();
          setShowCreateCard(false);
          return;
        }
      } catch (error) {
        onError(t("meetingNotes.selectMeeting.errors.createMeeting"), error);
      }
    }

    // Fallback: use local state if save fails
    setMeetings((prev) => [...prev, newMeeting]);
    setShowCreateCard(false);
  };

  const nextId =
    meetings.length > 0
      ? String(Math.max(...meetings.map((m) => parseInt(m.id) || 0)) + 1)
      : "1";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: "#242C63", fontWeight: 600 }}>
            {t("meetingNotes.selectMeeting.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5 }}>
            {t("meetingNotes.selectMeeting.description")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowCreateCard(true)}
          disabled={showCreateCard}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            textTransform: "none",
            px: 2,
            py: 0.75,
            boxShadow: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              boxShadow: "0 2px 6px rgba(36, 44, 99, 0.15)",
            },
            "&.Mui-disabled": {
              boxShadow: "none",
            },
          }}
        >
          {t("meetingNotes.selectMeeting.createNew")}
        </Button>
      </Box>
      {/* Create Meeting Card — animated entry/exit */}
      <Collapse
        in={showCreateCard}
        timeout={{ enter: 500, exit: 350 }}
        unmountOnExit
      >
        <Fade in={showCreateCard} timeout={{ enter: 220, exit: 160 }}>
          <Box sx={{ pb: 0.5 }}>
            <CreateMeetingCard
              onCreateMeeting={handleCreateMeeting}
              onCancel={() => setShowCreateCard(false)}
              nextId={nextId}
            />
          </Box>
        </Fade>
      </Collapse>
      {/* Existing meetings list */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {meetings.map((meeting, index) => {
          const isSelected = selectedMeeting?.id === meeting.id;
          const displayMeeting = getDisplayMeeting(meeting);
          return (
            <Box
              key={meeting.id}
              sx={{
                animation: `${cardEnter} 500ms cubic-bezier(0.22, 1, 0.36, 1) ${index * 110}ms both`,
              }}
            >
              <Card
                variant="outlined"
                onDoubleClick={() => onGoToTakeNotes(displayMeeting)}
                sx={[{
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  borderRadius: 4,
                  backgroundColor: "#FFFFFF",
                  transform: "",

                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: 3,
                    //  borderColor: alpha(theme.palette.primary.main, 0.4),
                  }
                }, isSelected ? {
                  borderColor: "#242c63d7"
                } : {
                  borderColor: null
                }]}
                onClick={() => onSelectMeeting(displayMeeting)}
              >
                <CardContent
                  sx={{
                    py: 2.5,
                    px: 3,
                    "&:last-child": { pb: 2.5 },
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: "#242C63" }}
                      >
                        {displayMeeting.title}
                      </Typography>
                      {displayMeeting.originalMeeting?.status &&
                        ["draft", "planned", "inreview"].includes(
                          displayMeeting.originalMeeting.status,
                        ) && (
                          <Chip
                            label={
                              displayMeeting.originalMeeting.status ===
                              "inreview"
                                ? t("library.inReview")
                                : t(
                                    `meeting_status.${displayMeeting.originalMeeting.status}`,
                                  )
                            }
                            size="small"
                            sx={[{
                              fontWeight: 500,
                              height: 22,
                              fontSize: "0.75rem"
                            }, displayMeeting.originalMeeting.status ===
                            "draft" ? {
                              backgroundColor: "#FFF7ED"
                            } : {
                              backgroundColor: displayMeeting.originalMeeting.status ===
                                    "inreview"
                                  ? "#EFF6FF"
                                  : "#F3F4F6"
                            }, displayMeeting.originalMeeting.status ===
                            "draft" ? {
                              color: "#EA580C"
                            } : {
                              color: displayMeeting.originalMeeting.status ===
                                    "inreview"
                                  ? "#2563EB"
                                  : "#374151"
                            }]}
                          />
                        )}
                    </Box>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          color: "#6B7280",
                          fontSize: "0.875rem",
                        }}
                      >
                        <EventOutlinedIcon sx={{ fontSize: 16 }} />
                        {formatLocalDateDisplay(displayMeeting.date)}
                      </Box>
                      {displayMeeting.startTime || displayMeeting.endTime ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            color: "#6B7280",
                            fontSize: "0.875rem",
                          }}
                        >
                          <AccessTimeOutlinedIcon sx={{ fontSize: 16 }} />
                          {formatMeetingTimeRange(
                            displayMeeting.startTime,
                            displayMeeting.endTime,
                          )}
                        </Box>
                      ) : null}
                      <Tooltip
                        title={
                          displayMeeting.originalMeeting?.participants?.length
                            ? displayMeeting.originalMeeting.participants.join(
                                ", ",
                              )
                            : ""
                        }
                        arrow
                        placement="top"
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            color: "#6B7280",
                            fontSize: "0.875rem",
                            cursor: "default",
                          }}
                        >
                          <PeopleOutlinedIcon sx={{ fontSize: 16 }} />
                          {t("meetingNotes.selectMeeting.participants", {
                            count: displayMeeting.attendees,
                          })}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Tooltip title={t("button.delete")}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setMeetingToDelete(displayMeeting);
                      }}
                      size="small"
                      sx={{
                        color: "#64748B",
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: "#FEE2E2",
                          color: "#DC2626",
                        },
                      }}
                    >
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </Tooltip>
                  <EditableMeetingInfo
                    meeting={displayMeeting}
                    onMeetingChange={handleCardMeetingChange}
                    layout="icon-only"
                  />
                  <IconButton
                    onClick={() => onGoToTakeNotes(displayMeeting)}
                    size="small"
                    sx={{
                      color: "#64748B",
                      flexShrink: 0,
                      "&:hover": {
                        backgroundColor: "#EDF0F4",
                        color: "#242C63",
                      },
                    }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </CardContent>
              </Card>
            </Box>
          );
        })}

        {meetings.length === 0 && !showCreateCard && (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: "#6B7280",
            }}
          >
            <Typography variant="body1">
              {t("meetingNotes.selectMeeting.empty")}
            </Typography>
          </Box>
        )}
      </Box>
      <ConfirmationPanel
        showModal={Boolean(meetingToDelete)}
        hideModal={() => setMeetingToDelete(null)}
        confirmModal={handleDeleteMeeting}
        entry={meetingToDelete}
        type="meeting"
        action="delete"
      />
    </Box>
  );
};

export default SelectMeetingStep;
